import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Typography, message } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import cccApi, { RetencaoClient as ApiRetencaoClient } from '../api/cccApi';
import {
  RETENCAO_ALL_SUBSTAGES,
  RETENCAO_SECTIONS,
  RETENCAO_SUBSTAGE_ENTRADA,
  RetencaoSectionKey,
  normalizeRetencaoSubstage,
  retencaoSectionForSubstage,
  retencaoSubstageLabel,
} from '../config/retencaoWorkspace';
import {
  CREATION_DATE_FILTERS,
  type CreationDatePresetKey,
  matchesCreationDateFrom,
  parseCreatedAt,
  presetCutoffPickerValue,
} from '../utils/workspaceCreationDateFilter';
import {
  canNavigateToMatch,
  fetchCccWorkspaceSearch,
  showCccSearchNotFound,
  showCccSearchSuccess,
} from '../utils/cccWorkspaceSearch';
import { useCccAuth } from '../contexts/CccAuthContext';
import { isCccCoordenadorOrAdmin } from '../utils/cccUserManagement';
import { isPlatformAdmin } from '../utils/isPlatformAdmin';
import WonderTradeLoader from '@crm/components/Shared/WonderTradeLoader';
import '@crm/components/Comercial/ComercialManagement.css';
import '../styles/prospeccao.css';
import '../styles/retencao.css';
import CccGestaoModalsOverlay, { type CccGestaoModalKind } from '../components/CccGestaoModalsOverlay';
import CccAlterarComercialModal from '../components/CccAlterarComercialModal';
import CccBulkSelectionBar from '../components/CccBulkSelectionBar';
import CccSolicitarPropostaRetencaoClientOverlay from '../components/CccSolicitarPropostaRetencaoClientOverlay';
import CccSubmeterContratoRetencaoClientOverlay from '../components/CccSubmeterContratoRetencaoClientOverlay';
import { isValidMarcacaoId } from '../config/prospeccaoMarcacao';

const { Text } = Typography;

type RetencaoClientRow = {
  id: string;
  company: string;
  nif: string;
  cancellationDate: string;
  cancellationReason: string;
  fine: string;
  objective: string;
  responseTime: string;
  substage: string;
  createdAt: string;
  updatedAt: string;
  createdBy: number | null;
  assignedTo: number | null;
};

type ClientDraft = Omit<
  RetencaoClientRow,
  'id' | 'substage' | 'createdAt' | 'updatedAt' | 'createdBy' | 'assignedTo'
>;

type WorkspaceUserOption = { id: number; name: string; username?: string; email?: string };

const CLIENTS_PER_PAGE = 10;

const emptyDraft: ClientDraft = {
  company: '',
  nif: '',
  cancellationDate: '',
  cancellationReason: '',
  fine: '',
  objective: '',
  responseTime: '',
};

const isValidRetencaoSubstage = (value?: string): boolean =>
  Boolean(value && RETENCAO_ALL_SUBSTAGES.includes(normalizeRetencaoSubstage(value)));

const fromApiClient = (raw: ApiRetencaoClient): RetencaoClientRow => ({
  id: String(raw.id),
  company: String(raw.nome_empresa || '').trim(),
  nif: String(raw.nif || '').trim(),
  cancellationDate: String(raw.data_cancelamento || '').slice(0, 10),
  cancellationReason: String(raw.motivo_cancelamento || '').trim(),
  fine: String(raw.multa || '').trim(),
  objective: String(raw.objetivo || '').trim(),
  responseTime: String(raw.tempo_resposta || '').trim(),
  substage: isValidRetencaoSubstage(raw.ccc_substage)
    ? normalizeRetencaoSubstage(raw.ccc_substage)
    : RETENCAO_SUBSTAGE_ENTRADA,
  createdAt: String(raw.created_at || new Date().toISOString()),
  updatedAt: String(raw.updated_at || new Date().toISOString()),
  createdBy: raw.created_by != null ? Number(raw.created_by) : null,
  assignedTo: raw.assigned_to != null ? Number(raw.assigned_to) : null,
});

const fmtDate = (iso?: string) => {
  if (!iso) return '—';
  try {
    const d = iso.length === 10 ? new Date(`${iso}T12:00:00`) : new Date(iso);
    return d.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const fmt = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const sortClientsByNewest = (a: RetencaoClientRow, b: RetencaoClientRow) => {
  const ta = parseCreatedAt(a.updatedAt)?.getTime() ?? 0;
  const tb = parseCreatedAt(b.updatedAt)?.getTime() ?? 0;
  return tb - ta;
};

const RetencaoBoard: React.FC = () => {
  const { user } = useCccAuth();
  const [loading, setLoading] = useState(true);
  const [pool, setPool] = useState<RetencaoClientRow[]>([]);
  const [workspaceClients, setWorkspaceClients] = useState<RetencaoClientRow[]>([]);
  const [activeSection, setActiveSection] = useState<RetencaoSectionKey>('por_contactar');
  const [expandedSubsections, setExpandedSubsections] = useState<Record<string, boolean>>({});
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [creationDateFrom, setCreationDateFrom] = useState('');
  const [poolHighlightId, setPoolHighlightId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarSearchTerm, setSidebarSearchTerm] = useState('');
  const [workspaceUsers, setWorkspaceUsers] = useState<WorkspaceUserOption[]>([]);
  const [workspaceUserId, setWorkspaceUserId] = useState<number | 'all' | null>(null);
  const [queueOpen, setQueueOpen] = useState(false);
  const [gestaoModal, setGestaoModal] = useState<CccGestaoModalKind | null>(null);
  const [gestaoModalNonce, setGestaoModalNonce] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState<ClientDraft>(emptyDraft);
  const [preparingProposta, setPreparingProposta] = useState(false);
  const [preparingContrato, setPreparingContrato] = useState(false);
  const [solicitarPropostaClient, setSolicitarPropostaClient] = useState<{
    id: string;
    company: string;
    nif: string;
    marcacaoId: string;
  } | null>(null);
  const [submeterContratoClient, setSubmeterContratoClient] = useState<{
    id: string;
    company: string;
    nif: string;
    marcacaoId: string;
  } | null>(null);
  const [subsectionPages, setSubsectionPages] = useState<Record<string, number>>({});
  const [workspaceFocus, setWorkspaceFocus] = useState<{ clientId: string; substage: string } | null>(
    null
  );
  const [bulkPoolSelectedIds, setBulkPoolSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkWorkspaceSelectedIds, setBulkWorkspaceSelectedIds] = useState<Set<string>>(() => new Set());
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [isMobileOrTablet] = useState(() => {
    if (typeof window === 'undefined') return false;
    const hasTouch =
      (typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) > 0) ||
      'ontouchstart' in window;
    const isTabletWidth = window.innerWidth <= 1100;
    const isSmallScreen = window.matchMedia('(max-width: 768px)').matches;
    return hasTouch && (isTabletWidth || isSmallScreen);
  });

  const canManageWorkspaceScope = useMemo(() => isCccCoordenadorOrAdmin(user), [user]);

  const canSeePool = useMemo(() => {
    if (isCccCoordenadorOrAdmin(user) || isPlatformAdmin(user)) return true;
    const role = String(user?.role || '').trim().toLowerCase();
    const tenant = String(user?.tenant || '').trim().toLowerCase();
    const businessArea = String(user?.business_area || '').trim().toLowerCase();
    if (role.includes('admin') || tenant === 'admin' || businessArea === 'admin') return true;
    return (
      role.includes('gerente') ||
      role.includes('manager') ||
      role.includes('coordenador') ||
      role.includes('coordinator')
    );
  }, [user]);

  const currentUserId = useMemo(() => {
    const raw = user?.id;
    if (raw === null || raw === undefined || raw === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cccApi.getRetencaoClients();
      if (!res.success) {
        message.error(res.message || 'Não foi possível carregar clientes de Reposição');
        setPool([]);
        setWorkspaceClients([]);
        return;
      }
      setPool((res.pool || []).map(fromApiClient).sort(sortClientsByNewest));
      setWorkspaceClients((res.clients || []).map(fromApiClient).sort(sortClientsByNewest));
    } catch (e) {
      console.error('[RetencaoBoard] load failed', e);
      message.error('Não foi possível carregar clientes de Reposição');
      setPool([]);
      setWorkspaceClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!canManageWorkspaceScope) {
      setWorkspaceUserId(currentUserId);
      return;
    }
    if (workspaceUserId === null) setWorkspaceUserId('all');
  }, [canManageWorkspaceScope, currentUserId, workspaceUserId]);

  useEffect(() => {
    if (!canManageWorkspaceScope && !canSeePool) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await cccApi.getCccDashboardUsers();
        if (cancelled || !res.success) return;
        const options = (res.users || [])
          .map((u) => ({
            id: Number(u.id),
            name: String(u.name || u.email || `Utilizador ${u.id}`),
            username: undefined as string | undefined,
            email: u.email ? String(u.email) : undefined,
          }))
          .filter((u: WorkspaceUserOption) => Number.isFinite(u.id))
          .sort((a, b) => a.name.localeCompare(b.name, 'pt'));
        setWorkspaceUsers(options);
      } catch {
        setWorkspaceUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageWorkspaceScope, canSeePool]);

  const filteredPool = useMemo(
    () => pool.filter((c) => matchesCreationDateFrom(c.createdAt, creationDateFrom)),
    [pool, creationDateFrom]
  );

  const filteredWorkspace = useMemo(() => {
    let scoped = workspaceClients;
    if (canManageWorkspaceScope) {
      if (workspaceUserId !== 'all' && workspaceUserId !== null) {
        scoped = scoped.filter((c) => c.assignedTo === workspaceUserId);
      }
    } else if (currentUserId) {
      scoped = scoped.filter((c) => c.assignedTo === currentUserId);
    }
    return scoped.filter((c) => matchesCreationDateFrom(c.createdAt, creationDateFrom));
  }, [workspaceClients, creationDateFrom, canManageWorkspaceScope, workspaceUserId, currentUserId]);

  useEffect(() => {
    setSubsectionPages({});
  }, [creationDateFrom, workspaceUserId, activeSection]);

  const fechoCount = useMemo(
    () =>
      filteredWorkspace.filter(
        (c) => retencaoSectionForSubstage(normalizeRetencaoSubstage(c.substage))?.key === 'fecho'
      ).length,
    [filteredWorkspace]
  );

  const stats = useMemo(
    () => ({
      pool: filteredPool.length,
      workspace: filteredWorkspace.length,
      fecho: fechoCount,
    }),
    [filteredPool.length, filteredWorkspace.length, fechoCount]
  );

  const workspaceSelectionLabel = useMemo(() => {
    if (!canManageWorkspaceScope) return '';
    if (workspaceUserId === 'all' || workspaceUserId === null) return 'Toda a Equipa';
    const match = workspaceUsers.find((u) => u.id === workspaceUserId);
    return match ? match.name : 'Utilizador selecionado';
  }, [canManageWorkspaceScope, workspaceUserId, workspaceUsers]);

  const filteredSidebarUsers = useMemo(() => {
    if (!sidebarSearchTerm.trim()) return workspaceUsers;
    const searchLower = sidebarSearchTerm.toLowerCase();
    return workspaceUsers.filter((u) => {
      const name = (u.name || u.username || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return name.includes(searchLower) || email.includes(searchLower);
    });
  }, [workspaceUsers, sidebarSearchTerm]);

  const bySubstage = useMemo(() => {
    const map: Record<string, RetencaoClientRow[]> = {};
    RETENCAO_SECTIONS.forEach((section) => {
      section.subsections.forEach((sub) => {
        map[sub.substage] = [];
      });
    });
    filteredWorkspace.forEach((client) => {
      const normalized = normalizeRetencaoSubstage(client.substage);
      if (map[normalized]) map[normalized].push(client);
    });
    Object.keys(map).forEach((key) => {
      map[key].sort(sortClientsByNewest);
    });
    return map;
  }, [filteredWorkspace]);

  const sectionCounts = useMemo(() => {
    const counts: Record<RetencaoSectionKey, number> = {
      por_contactar: 0,
      em_tratamento: 0,
      pre_fecho: 0,
      fecho: 0,
    };
    RETENCAO_SECTIONS.forEach((section) => {
      counts[section.key] = section.subsections.reduce(
        (total, sub) => total + (bySubstage[sub.substage]?.length || 0),
        0
      );
    });
    return counts;
  }, [bySubstage]);

  const activeConfig = RETENCAO_SECTIONS.find((section) => section.key === activeSection)!;
  const selectedClient =
    [...workspaceClients, ...pool].find((c) => c.id === selectedClientId) || null;

  useEffect(() => {
    if (!workspaceFocus) return;
    const { clientId, substage } = workspaceFocus;
    const normalized = normalizeRetencaoSubstage(substage);
    const section = retencaoSectionForSubstage(normalized);
    if (section) setActiveSection(section.key);
    setExpandedSubsections((prev) => ({ ...prev, [normalized]: true }));
    const rows = bySubstage[normalized] || [];
    const idx = rows.findIndex((c) => c.id === clientId);
    if (idx >= 0) {
      setSubsectionPages((prev) => ({ ...prev, [normalized]: Math.floor(idx / CLIENTS_PER_PAGE) + 1 }));
    }
    setSelectedClientId(clientId);
    const timer = window.setTimeout(() => setWorkspaceFocus(null), 0);
    return () => window.clearTimeout(timer);
  }, [workspaceFocus, bySubstage]);

  const setSubsectionPage = (substage: string, page: number) => {
    setSubsectionPages((prev) => ({ ...prev, [substage]: Math.max(1, page) }));
  };

  const applyCreationDatePreset = (key: CreationDatePresetKey) => {
    setCreationDateFrom(presetCutoffPickerValue(key));
  };

  const clearFilters = () => {
    setSearch('');
    setCreationDateFrom('');
    setPoolHighlightId(null);
    if (canManageWorkspaceScope) {
      setWorkspaceUserId('all');
    } else if (currentUserId) {
      setWorkspaceUserId(currentUserId);
    }
    setSubsectionPages({});
  };

  const handleWorkspaceSearch = async () => {
    const q = search.trim();
    if (!q) {
      message.warning('Introduza empresa, contacto ou NIF para pesquisar.');
      return;
    }
    try {
      const matches = await fetchCccWorkspaceSearch('retencao', q);
      if (!matches.length) {
        showCccSearchNotFound(q);
        return;
      }
      const match = matches[0];
      const nav = canNavigateToMatch(match, { canManageWorkspaceScope, currentUserId });
      if (!nav.ok) {
        message.warning(nav.reason);
        return;
      }
      setCreationDateFrom('');
      setPoolHighlightId(null);
      if (canManageWorkspaceScope) {
        setWorkspaceUserId(match.owner_user_id ?? 'all');
      }
      await load();
      if (match.is_available) {
        if (canSeePool) {
          setQueueOpen(true);
          setPoolHighlightId(match.id);
          window.requestAnimationFrame(() => {
            document
              .querySelector(`[data-pool-client-id="${match.id}"]`)
              ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          });
        }
        showCccSearchSuccess(match);
        if (matches.length > 1) {
          message.info(`Foram encontrados ${matches.length} resultados. A abrir o primeiro (Pool).`);
        }
        return;
      }
      setWorkspaceFocus({
        clientId: match.id,
        substage: normalizeRetencaoSubstage(match.substage),
      });
      showCccSearchSuccess(match);
      if (matches.length > 1) {
        message.info(`Foram encontrados ${matches.length} resultados. A abrir o primeiro.`);
      }
    } catch (err: unknown) {
      const msg =
        (err as { message?: string })?.message ||
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao pesquisar';
      message.error(msg);
    }
  };

  const patchClient = (id: string, patch: Partial<RetencaoClientRow>) => {
    const updater = (rows: RetencaoClientRow[]) =>
      rows.map((row) =>
        row.id === id
          ? { ...row, ...patch, updatedAt: new Date().toISOString() }
          : row
      );
    setWorkspaceClients(updater);
    setPool(updater);
  };

  const replaceClient = (normalized: RetencaoClientRow) => {
    if (normalized.assignedTo == null) {
      setWorkspaceClients((prev) => prev.filter((c) => c.id !== normalized.id));
      setPool((prev) => {
        const exists = prev.some((c) => c.id === normalized.id);
        const next = exists
          ? prev.map((c) => (c.id === normalized.id ? normalized : c))
          : [normalized, ...prev];
        return next.sort(sortClientsByNewest);
      });
      return;
    }
    setPool((prev) => prev.filter((c) => c.id !== normalized.id));
    setWorkspaceClients((prev) => {
      const exists = prev.some((c) => c.id === normalized.id);
      const next = exists
        ? prev.map((c) => (c.id === normalized.id ? normalized : c))
        : [normalized, ...prev];
      return next.sort(sortClientsByNewest);
    });
  };

  const saveClient = async (client: RetencaoClientRow) => {
    try {
      const res = await cccApi.updateRetencaoClient(client.id, {
        nome_empresa: client.company,
        nif: client.nif,
        data_cancelamento: client.cancellationDate,
        motivo_cancelamento: client.cancellationReason || null,
        multa: client.fine || null,
        objetivo: client.objective || null,
        tempo_resposta: client.responseTime || null,
      });
      if (!res.success || !res.client) {
        message.error(res.message || 'Não foi possível guardar o cliente.');
        return;
      }
      const normalized = fromApiClient(res.client);
      replaceClient(normalized);
      message.success('Cliente guardado.');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao guardar cliente';
      message.error(msg);
    }
  };

  const changeClientSubstage = async (clientId: string, substage: string) => {
    try {
      const res = await cccApi.updateRetencaoClientSubstage(clientId, substage);
      if (!res.success || !res.client) {
        message.error(res.message || 'Não foi possível atualizar o sub-estágio.');
        return;
      }
      const normalized = fromApiClient(res.client);
      replaceClient(normalized);
      const section = retencaoSectionForSubstage(substage);
      if (section) setActiveSection(section.key);
      setExpandedSubsections((prev) => ({ ...prev, [substage]: true }));
      message.success('Estado atualizado');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao atualizar sub-estágio';
      message.error(msg);
    }
  };

  const isClientMine = (client: RetencaoClientRow) =>
    currentUserId != null && client.assignedTo === currentUserId;

  const canRequestOnClient = (client: RetencaoClientRow) => {
    if (client.assignedTo == null) return false;
    return isClientMine(client) || canManageWorkspaceScope || canSeePool;
  };

  const handleSolicitarProposta = async (client: RetencaoClientRow) => {
    if (!canRequestOnClient(client)) {
      message.warning(
        client.assignedTo == null
          ? 'Atribua o cliente antes de solicitar proposta.'
          : 'O cliente tem de estar atribuído a si antes de solicitar proposta.'
      );
      return;
    }
    setPreparingProposta(true);
    try {
      const res = await cccApi.prepareRetencaoClientSolicitarProposta(client.id);
      if (!res.success) {
        message.error(
          res.message || 'Não foi possível atualizar o estado para "A aguardar Proposta BO".'
        );
        return;
      }
      const marcacaoId = isValidMarcacaoId(res.marcacao_id)
        ? String(res.marcacao_id).trim()
        : '';
      if (!marcacaoId) {
        message.error('Não foi possível obter o ID da marcação para Solicitar Proposta.');
        return;
      }
      if (res.client) {
        const normalized = fromApiClient(res.client);
        replaceClient(normalized);
        const section = retencaoSectionForSubstage(normalized.substage);
        if (section) setActiveSection(section.key);
        setExpandedSubsections((prev) => ({ ...prev, [normalized.substage]: true }));
      }
      setSolicitarPropostaClient({
        id: client.id,
        company: client.company,
        nif: client.nif,
        marcacaoId,
      });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao atualizar estado antes de abrir Solicitar Proposta';
      message.error(msg);
    } finally {
      setPreparingProposta(false);
    }
  };

  const handleProposalSuccess = async () => {
    setSolicitarPropostaClient(null);
    await load();
    message.success('Pedido de proposta submetido.');
  };

  const handleSubmeterContrato = async (client: RetencaoClientRow) => {
    if (!canRequestOnClient(client)) {
      message.warning(
        client.assignedTo == null
          ? 'Atribua o cliente antes de submeter contrato.'
          : 'O cliente tem de estar atribuído a si antes de submeter contrato.'
      );
      return;
    }
    setPreparingContrato(true);
    try {
      const res = await cccApi.getRetencaoClientMarcacao(client.id);
      if (!res.success) {
        message.error(res.message || 'Não foi possível carregar a marcação para submeter contrato.');
        return;
      }
      const marcacaoId = isValidMarcacaoId(res.marcacao_id) ? String(res.marcacao_id).trim() : '';
      if (!marcacaoId) {
        message.error('Não foi possível obter o ID da marcação para Submeter Contrato.');
        return;
      }
      setSubmeterContratoClient({
        id: client.id,
        company: client.company,
        nif: client.nif,
        marcacaoId,
      });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao abrir Submeter Contrato';
      message.error(msg);
    } finally {
      setPreparingContrato(false);
    }
  };

  const handleContractSuccess = async () => {
    setSubmeterContratoClient(null);
    await load();
    message.success('Contrato submetido.');
  };

  const createClient = async () => {
    const company = draft.company.trim();
    const nif = draft.nif.trim();
    const cancellationDate = draft.cancellationDate.trim();
    if (!company || !nif || !cancellationDate) {
      message.error('Nome do Cliente, NIF e Data do Cancelamento são obrigatórios.');
      return;
    }
    try {
      const res = await cccApi.createRetencaoClient({
        nome_empresa: company,
        nif,
        data_cancelamento: cancellationDate,
        motivo_cancelamento: draft.cancellationReason.trim() || null,
        multa: draft.fine.trim() || null,
        objetivo: draft.objective.trim() || null,
        tempo_resposta: draft.responseTime.trim() || null,
        source: 'create',
      });
      if (!res.success || !res.client) {
        message.error(res.message || 'Não foi possível criar o cliente.');
        return;
      }
      const client = fromApiClient(res.client);
      await load();
      if (client.assignedTo == null && canSeePool) {
        setQueueOpen(true);
        setPoolHighlightId(client.id);
      } else {
        setSelectedClientId(client.id);
        const section = retencaoSectionForSubstage(client.substage);
        if (section) setActiveSection(section.key);
        setExpandedSubsections((prev) => ({ ...prev, [client.substage]: true }));
        setWorkspaceFocus({ clientId: client.id, substage: client.substage });
      }
      setDraft(emptyDraft);
      setShowCreate(false);
      message.success('Cliente criado com sucesso.');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao criar cliente';
      message.error(msg);
    }
  };

  const toggleBulkPoolSelect = useCallback((clientId: string, checked: boolean) => {
    setBulkPoolSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(clientId);
      else next.delete(clientId);
      return next;
    });
  }, []);

  const toggleBulkPoolPage = useCallback((clientIds: string[], checked: boolean) => {
    setBulkPoolSelectedIds((prev) => {
      const next = new Set(prev);
      clientIds.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, []);

  const toggleBulkWorkspaceSelect = useCallback((clientId: string, checked: boolean) => {
    setBulkWorkspaceSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(clientId);
      else next.delete(clientId);
      return next;
    });
  }, []);

  const toggleBulkWorkspacePage = useCallback((clientIds: string[], checked: boolean) => {
    setBulkWorkspaceSelectedIds((prev) => {
      const next = new Set(prev);
      clientIds.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, []);

  const clearBulkPoolSelection = useCallback(() => setBulkPoolSelectedIds(new Set()), []);
  const clearBulkWorkspaceSelection = useCallback(() => setBulkWorkspaceSelectedIds(new Set()), []);

  const openAssignModal = useCallback(() => {
    if (bulkPoolSelectedIds.size === 0) {
      message.warning('Selecione pelo menos um cliente no Pool.');
      return;
    }
    setAssignModalOpen(true);
  }, [bulkPoolSelectedIds.size]);

  const openReassignModal = useCallback(() => {
    if (bulkWorkspaceSelectedIds.size === 0) {
      message.warning('Selecione pelo menos um cliente na área de trabalho.');
      return;
    }
    setReassignModalOpen(true);
  }, [bulkWorkspaceSelectedIds.size]);

  const handleConfirmAssign = async (userId: number) => {
    const ids = Array.from(bulkPoolSelectedIds);
    if (!ids.length) return;
    setBulkSubmitting(true);
    try {
      const res = await cccApi.bulkAssignRetencaoClients(ids, userId);
      const count = res.updated_count ?? res.updated?.length ?? 0;
      if (count > 0) {
        message.success(
          count === 1 ? 'Cliente atribuído com sucesso.' : `${count} clientes atribuídos.`
        );
        clearBulkPoolSelection();
        setAssignModalOpen(false);
        await load();
      } else {
        message.warning('Nenhum cliente foi atribuído.');
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível atribuir os clientes';
      message.error(msg);
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleConfirmReassign = async (userId: number) => {
    const ids = Array.from(bulkWorkspaceSelectedIds);
    if (!ids.length) return;
    setBulkSubmitting(true);
    try {
      const res = await cccApi.bulkReassignRetencaoClients(ids, userId);
      const count = res.updated_count ?? res.updated?.length ?? 0;
      if (count > 0) {
        message.success(
          count === 1
            ? 'Cliente reatribuído com sucesso.'
            : `${count} clientes reatribuídos.`
        );
        clearBulkWorkspaceSelection();
        setReassignModalOpen(false);
        await load();
      } else {
        message.warning('Nenhum cliente foi reatribuído.');
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível reatribuir os clientes';
      message.error(msg);
    } finally {
      setBulkSubmitting(false);
    }
  };

  if (loading && pool.length === 0 && workspaceClients.length === 0) {
    return (
      <div className="rec-page retencao-page-scope">
        <WonderTradeLoader message="A carregar Reposição..." />
      </div>
    );
  }

  return (
    <div className="retencao-page-scope gestao-marcacoes prosp-page-scope rec-page">
      <header className="rec-hero rec-hero--compact">
        <div className="rec-hero-top">
          <div className="rec-hero-text">
            <span className="rec-hero-eyebrow">CallCenter Comercial</span>
            <div
              className="gestao-marcacoes-header-title-row prosp-hero-title-row"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}
            >
              {canManageWorkspaceScope && (
                <Button
                  type="text"
                  className="gestao-marcacoes-sidebar-trigger"
                  icon={<TeamOutlined style={{ fontSize: 10 }} />}
                  onClick={() => setDrawerOpen(true)}
                  aria-label="Abrir seleção de utilizador da equipa CCC"
                  style={{
                    backgroundColor: '#ff4d4f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '3px',
                    padding: 0,
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    minHeight: 20,
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    boxShadow: '0 1px 2px rgba(255, 77, 79, 0.22)',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ff7875';
                    e.currentTarget.style.boxShadow = '0 2px 5px rgba(255, 77, 79, 0.32)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ff4d4f';
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(255, 77, 79, 0.22)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                />
              )}
              <h1 className="rec-hero-title">Reposição</h1>
              {canManageWorkspaceScope && workspaceSelectionLabel && (
                <div className="management-viewing-user">
                  <div
                    className="user-color management-user-dot"
                    style={{
                      backgroundColor:
                        workspaceUserId === 'all' || workspaceUserId === null
                          ? '#1890ff'
                          : Number(workspaceUserId) % 2 === 0
                            ? '#ff001e'
                            : '#cccccc',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}
                  />
                  <span className="management-user-indicator">{workspaceSelectionLabel}</span>
                </div>
              )}
            </div>
          </div>
          <div className="rec-kpis">
            {canSeePool && (
              <div className="rec-kpi">
                <span className="rec-kpi-value">{stats.pool}</span>
                <span className="rec-kpi-label">Pool</span>
              </div>
            )}
            <div className="rec-kpi">
              <span className="rec-kpi-value">{stats.workspace}</span>
              <span className="rec-kpi-label">Na área de trabalho</span>
            </div>
            <div className="rec-kpi">
              <span className="rec-kpi-value rec-kpi-value--fecho">{stats.fecho}</span>
              <span className="rec-kpi-label">Fecho</span>
            </div>
          </div>
        </div>
        <div className="rec-toolbar">
          <div className="rec-toolbar-group">
            <input
              type="search"
              className="rec-input"
              placeholder="Pesquisar empresa, contacto ou NIF…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void handleWorkspaceSearch()}
            />
            <button type="button" className="rec-btn rec-btn-ghost" onClick={() => void handleWorkspaceSearch()}>
              Procurar
            </button>
            <div className="prosp-date-filters" role="group" aria-label="Filtrar por data de criação">
              <span className="prosp-date-filters-label">Data de criação</span>
              <div className="prosp-date-filters-buttons">
                {CREATION_DATE_FILTERS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`prosp-date-filter-btn ${
                      creationDateFrom === presetCutoffPickerValue(option.key) ? 'is-active' : ''
                    }`}
                    onClick={() => applyCreationDatePreset(option.key)}
                    aria-pressed={creationDateFrom === presetCutoffPickerValue(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <label className="prosp-date-picker-field">
                <span className="prosp-date-filters-label">A partir de</span>
                <input
                  type="date"
                  className={`rec-input prosp-date-picker ${creationDateFrom ? 'is-active' : ''}`}
                  value={creationDateFrom}
                  onChange={(e) => setCreationDateFrom(e.target.value)}
                  aria-label="Filtrar clientes criados a partir desta data"
                />
              </label>
            </div>
          </div>
          <div className="rec-toolbar-group rec-toolbar-group--actions">
            <button type="button" className="rec-btn rec-btn-ghost" onClick={clearFilters}>
              Limpar
            </button>
            <button type="button" className="rec-btn rec-btn-primary" onClick={() => void load()}>
              Atualizar
            </button>
            <button
              type="button"
              className="rec-btn rec-btn-minhas-propostas"
              onClick={() => {
                setGestaoModal('minhas-propostas');
                setGestaoModalNonce((n) => n + 1);
              }}
            >
              Minhas Propostas
            </button>
            <button
              type="button"
              className="rec-btn rec-btn-meus-contratos"
              onClick={() => {
                setGestaoModal('meus-contratos');
                setGestaoModalNonce((n) => n + 1);
              }}
            >
              Meus Contratos
            </button>
            <button type="button" className="rec-btn rec-btn-primary" onClick={() => setShowCreate(true)}>
              + Criar Cliente
            </button>
            {canSeePool && (
              <button
                type="button"
                className="rec-btn rec-btn-ghost"
                disabled
                title="Em breve"
              >
                Upload (em breve)
              </button>
            )}
            {canSeePool && (
              <>
                <button
                  type="button"
                  className="rec-btn rec-btn-ghost"
                  onClick={openAssignModal}
                >
                  Atribuir
                  {bulkPoolSelectedIds.size > 0 ? ` (${bulkPoolSelectedIds.size})` : ''}
                </button>
                <button
                  type="button"
                  className="rec-btn rec-btn-ghost rec-btn-alterar-comercial"
                  onClick={openReassignModal}
                >
                  Reatribuir
                  {bulkWorkspaceSelectedIds.size > 0 ? ` (${bulkWorkspaceSelectedIds.size})` : ''}
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {canSeePool && bulkWorkspaceSelectedIds.size > 0 && (
        <CccBulkSelectionBar
          selectedCount={bulkWorkspaceSelectedIds.size}
          onClear={clearBulkWorkspaceSelection}
          onAlterarComercial={openReassignModal}
        />
      )}

      <div
        className={`rec-workspace ${
          !canSeePool || queueOpen ? '' : 'rec-workspace--queue-closed'
        }`}
      >
        {canSeePool && (
          <aside className={`rec-queue-rail ${queueOpen ? 'is-open' : 'is-collapsed'}`}>
            {queueOpen ? (
              <>
                <div className="rec-queue-head">
                  <div className="rec-queue-head-row">
                    <div className="rec-queue-head-label">
                      <h2 className="rec-queue-title">Pool</h2>
                      <span className="rec-badge">{filteredPool.length}</span>
                    </div>
                    <button
                      type="button"
                      className="rec-queue-collapse-btn"
                      onClick={() => setQueueOpen(false)}
                      aria-label="Recolher painel Pool"
                      title="Recolher"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                        <path
                          d="M10 3L5 8L10 13"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <p className="rec-queue-hint">
                    Selecione clientes e use Atribuir para enviar à área de trabalho.
                  </p>
                  <div className="rec-queue-actions">
                    <button
                      type="button"
                      className="rec-btn rec-btn-primary rec-btn-sm"
                      onClick={openAssignModal}
                      disabled={bulkPoolSelectedIds.size === 0}
                    >
                      Atribuir
                      {bulkPoolSelectedIds.size > 0 ? ` (${bulkPoolSelectedIds.size})` : ''}
                    </button>
                    {filteredPool.length > 0 && (
                      <label className="rec-queue-select-all">
                        <input
                          type="checkbox"
                          className="ccc-bulk-checkbox"
                          checked={
                            filteredPool.length > 0 &&
                            filteredPool.every((c) => bulkPoolSelectedIds.has(c.id))
                          }
                          ref={(el) => {
                            if (!el) return;
                            const some = filteredPool.some((c) => bulkPoolSelectedIds.has(c.id));
                            const all =
                              filteredPool.length > 0 &&
                              filteredPool.every((c) => bulkPoolSelectedIds.has(c.id));
                            el.indeterminate = some && !all;
                          }}
                          onChange={(e) =>
                            toggleBulkPoolPage(
                              filteredPool.map((c) => c.id),
                              e.target.checked
                            )
                          }
                        />
                        <span>Selecionar todos</span>
                      </label>
                    )}
                  </div>
                </div>
                <div className="rec-queue-list">
                  {filteredPool.length === 0 ? (
                    <p className="rec-queue-empty">Nenhum cliente no Pool.</p>
                  ) : (
                    filteredPool.map((client) => {
                      const checked = bulkPoolSelectedIds.has(client.id);
                      return (
                        <div
                          key={client.id}
                          data-pool-client-id={client.id}
                          className={
                            poolHighlightId === client.id
                              ? 'rec-queue-item-wrap is-search-hit'
                              : 'rec-queue-item-wrap'
                          }
                        >
                          <label className="rec-queue-pool-card">
                            <input
                              type="checkbox"
                              className="ccc-bulk-checkbox"
                              checked={checked}
                              onChange={(e) => toggleBulkPoolSelect(client.id, e.target.checked)}
                              aria-label={`Selecionar ${client.company}`}
                            />
                            <span className="rec-queue-pool-card-body">
                              <span className="rec-queue-pool-empresa">{client.company || '—'}</span>
                              <span className="rec-queue-pool-meta">
                                {[client.nif, fmtDate(client.cancellationDate)].filter(Boolean).join(' · ') ||
                                  '—'}
                              </span>
                              {client.nif && (
                                <span className="rec-queue-pool-nif">NIF {client.nif}</span>
                              )}
                            </span>
                          </label>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            ) : (
              <button
                type="button"
                className="rec-queue-rail-collapsed"
                onClick={() => setQueueOpen(true)}
                aria-label={`Expandir Pool (${filteredPool.length})`}
                title="Expandir Pool"
              >
                <span className="rec-queue-rail-accent" aria-hidden />
                <span className="rec-queue-rail-collapsed-inner">
                  <span className="rec-queue-rail-expand-icon">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                      <path
                        d="M6 3L11 8L6 13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span className="rec-queue-rail-vertical-label">Pool</span>
                  <span className="rec-queue-rail-count">{filteredPool.length}</span>
                </span>
              </button>
            )}
          </aside>
        )}

        <main className="rec-desk">
          <div className="rec-ws">
            <nav className="rec-ws-tabs" aria-label="Secções da área de trabalho">
              {RETENCAO_SECTIONS.map((section) => (
                <button
                  key={section.key}
                  type="button"
                  className={`rec-ws-tab ${activeSection === section.key ? 'is-active' : ''}`}
                  onClick={() => setActiveSection(section.key)}
                >
                  <span className="rec-ws-tab-dot" style={{ background: section.accent }} />
                  {section.title}
                  <span className="rec-ws-tab-count">{sectionCounts[section.key]}</span>
                </button>
              ))}
            </nav>

            <div className="rec-accordions">
              {activeConfig.subsections.map((sub) => {
                const rows = bySubstage[sub.substage] || [];
                const totalRows = rows.length;
                const totalPages = Math.max(1, Math.ceil(totalRows / CLIENTS_PER_PAGE));
                const requestedPage = subsectionPages[sub.substage] || 1;
                const currentPage = Math.min(Math.max(1, requestedPage), totalPages);
                const pageStart = totalRows === 0 ? 0 : (currentPage - 1) * CLIENTS_PER_PAGE + 1;
                const pageEnd = Math.min(currentPage * CLIENTS_PER_PAGE, totalRows);
                const paginatedRows = rows.slice(
                  (currentPage - 1) * CLIENTS_PER_PAGE,
                  currentPage * CLIENTS_PER_PAGE
                );
                const isOpen = expandedSubsections[sub.substage] === true;
                return (
                  <section key={sub.substage} className={`rec-accordion ${isOpen ? 'is-open' : ''}`}>
                    <button
                      type="button"
                      className="rec-accordion-trigger"
                      onClick={() =>
                        setExpandedSubsections((prev) => ({
                          ...prev,
                          [sub.substage]: !prev[sub.substage],
                        }))
                      }
                      aria-expanded={isOpen}
                    >
                      <span className="rec-accordion-chevron">▼</span>
                      <span>{sub.label}</span>
                      <span className="rec-accordion-count">{totalRows}</span>
                    </button>
                    {(isOpen || loading) && (
                      <div className="rec-accordion-body">
                        {loading ? (
                          <div className="prosp-accordion-loading">
                            <WonderTradeLoader message="A carregar clientes..." />
                          </div>
                        ) : totalRows === 0 ? (
                          <p className="rec-accordion-empty">Sem clientes neste estado.</p>
                        ) : (
                          <>
                            <div className="rec-table-scroll">
                              <table className="rec-table rec-table--selectable">
                                <thead>
                                  <tr>
                                    {canSeePool && (
                                      <th className="rec-table-col-bulk" scope="col">
                                        <input
                                          type="checkbox"
                                          className="ccc-bulk-checkbox"
                                          aria-label="Selecionar todos nesta página"
                                          checked={
                                            paginatedRows.length > 0 &&
                                            paginatedRows.every((c) => bulkWorkspaceSelectedIds.has(c.id))
                                          }
                                          ref={(el) => {
                                            if (!el) return;
                                            const some = paginatedRows.some((c) =>
                                              bulkWorkspaceSelectedIds.has(c.id)
                                            );
                                            const all =
                                              paginatedRows.length > 0 &&
                                              paginatedRows.every((c) =>
                                                bulkWorkspaceSelectedIds.has(c.id)
                                              );
                                            el.indeterminate = some && !all;
                                          }}
                                          onChange={(e) =>
                                            toggleBulkWorkspacePage(
                                              paginatedRows.map((c) => c.id),
                                              e.target.checked
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </th>
                                    )}
                                    <th className="rec-table-col-chevron" aria-hidden />
                                    <th>Empresa / Responsável</th>
                                    <th>Última atualização</th>
                                    <th>Contacto / E-mail</th>
                                    <th>Estado</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {paginatedRows.map((client) => {
                                    const isSelected = selectedClientId === client.id;
                                    const isBulkChecked = bulkWorkspaceSelectedIds.has(client.id);
                                    return (
                                      <React.Fragment key={client.id}>
                                        <tr
                                          className={`rec-table-row ${isSelected ? 'is-selected' : ''} ${
                                            isBulkChecked ? 'is-bulk-selected' : ''
                                          }`}
                                          onClick={() =>
                                            setSelectedClientId(isSelected ? null : client.id)
                                          }
                                        >
                                          {canSeePool && (
                                            <td
                                              className="rec-table-col-bulk"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <input
                                                type="checkbox"
                                                className="ccc-bulk-checkbox"
                                                aria-label={`Selecionar ${client.company}`}
                                                checked={isBulkChecked}
                                                onChange={(e) =>
                                                  toggleBulkWorkspaceSelect(client.id, e.target.checked)
                                                }
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                            </td>
                                          )}
                                          <td className="rec-table-col-chevron">
                                            <span
                                              className={`rec-row-chevron ${isSelected ? 'is-open' : ''}`}
                                            >
                                              ▶
                                            </span>
                                          </td>
                                          <td className="rec-table-cell-empresa">
                                            <span className="rec-table-empresa-line">
                                              <span className="rec-table-empresa">{client.company}</span>
                                            </span>
                                            <span className="rec-table-responsavel">
                                              NIF {client.nif || '—'}
                                            </span>
                                          </td>
                                          <td>
                                            <span className="rec-table-schedule">
                                              {fmtDate(client.cancellationDate)}
                                            </span>
                                          </td>
                                          <td>
                                            <span className="rec-table-contacto">
                                              {client.cancellationReason || '—'}
                                            </span>
                                            {client.fine && (
                                              <span className="rec-table-extra">Multa: {client.fine}</span>
                                            )}
                                          </td>
                                          <td>
                                            <span className="rec-table-estado-pill">
                                              {retencaoSubstageLabel(client.substage)}
                                            </span>
                                          </td>
                                        </tr>
                                        {isSelected && (
                                          <tr className="rec-table-detail-row">
                                            <td colSpan={canSeePool ? 6 : 5}>
                                              <div className="rec-lead-panel prosp-lead-panel">
                                                <div className="rec-lead-panel-bar">
                                                  <div className="rec-lead-panel-bar-title">
                                                    <span className="rec-lead-panel-empresa-line">
                                                      <span className="rec-lead-panel-empresa">
                                                        {(selectedClient?.company || client.company) ||
                                                          '—'}
                                                      </span>
                                                    </span>
                                                    <span className="rec-lead-panel-meta">
                                                      NIF {(selectedClient?.nif || client.nif) || '—'}
                                                      {' · '}
                                                      Cancelamento{' '}
                                                      {fmtDate(
                                                        selectedClient?.cancellationDate ||
                                                          client.cancellationDate
                                                      )}
                                                      {' · '}
                                                      {retencaoSubstageLabel(
                                                        selectedClient?.substage || client.substage
                                                      )}
                                                    </span>
                                                  </div>
                                                  <button
                                                    type="button"
                                                    className="rec-lead-panel-collapse"
                                                    onClick={() => setSelectedClientId(null)}
                                                    title="Fechar ficha"
                                                  >
                                                    <span>Fechar ficha</span>
                                                    <span
                                                      className="rec-lead-panel-collapse-icon"
                                                      aria-hidden
                                                    >
                                                      ▼
                                                    </span>
                                                  </button>
                                                </div>
                                                <div className="rec-lead-panel-body">
                                                  <div className="rec-lead-panel-main">
                                                    <section className="rec-lead-detail-group prosp-lead-card">
                                                      <h4 className="rec-lead-detail-group-title">
                                                        Dados do cliente
                                                      </h4>
                                                      <div className="rec-envio-proposta-fields prosp-lead-fields">
                                                        <label className="rec-envio-proposta-field">
                                                          <span className="rec-envio-proposta-label">
                                                            Nome do Cliente{' '}
                                                            <span className="rec-required-asterisk">*</span>
                                                          </span>
                                                          <input
                                                            className="rec-envio-proposta-input"
                                                            value={selectedClient?.company || ''}
                                                            onChange={(e) =>
                                                              patchClient(client.id, {
                                                                company: e.target.value,
                                                              })
                                                            }
                                                          />
                                                        </label>
                                                        <label className="rec-envio-proposta-field">
                                                          <span className="rec-envio-proposta-label">
                                                            NIF{' '}
                                                            <span className="rec-required-asterisk">*</span>
                                                          </span>
                                                          <input
                                                            className="rec-envio-proposta-input"
                                                            value={selectedClient?.nif || ''}
                                                            onChange={(e) =>
                                                              patchClient(client.id, {
                                                                nif: e.target.value,
                                                              })
                                                            }
                                                          />
                                                        </label>
                                                        <label className="rec-envio-proposta-field">
                                                          <span className="rec-envio-proposta-label">
                                                            Data do Cancelamento{' '}
                                                            <span className="rec-required-asterisk">*</span>
                                                          </span>
                                                          <input
                                                            type="date"
                                                            className="rec-envio-proposta-input"
                                                            value={selectedClient?.cancellationDate || ''}
                                                            onChange={(e) =>
                                                              patchClient(client.id, {
                                                                cancellationDate: e.target.value,
                                                              })
                                                            }
                                                          />
                                                        </label>
                                                        <label className="rec-envio-proposta-field">
                                                          <span className="rec-envio-proposta-label">
                                                            Motivo de Cancelamento
                                                          </span>
                                                          <input
                                                            className="rec-envio-proposta-input"
                                                            value={
                                                              selectedClient?.cancellationReason || ''
                                                            }
                                                            onChange={(e) =>
                                                              patchClient(client.id, {
                                                                cancellationReason: e.target.value,
                                                              })
                                                            }
                                                          />
                                                        </label>
                                                        <label className="rec-envio-proposta-field">
                                                          <span className="rec-envio-proposta-label">Multa</span>
                                                          <input
                                                            className="rec-envio-proposta-input"
                                                            value={selectedClient?.fine || ''}
                                                            onChange={(e) =>
                                                              patchClient(client.id, {
                                                                fine: e.target.value,
                                                              })
                                                            }
                                                          />
                                                        </label>
                                                        <label className="rec-envio-proposta-field">
                                                          <span className="rec-envio-proposta-label">Objectivo</span>
                                                          <textarea
                                                            className="rec-envio-proposta-input"
                                                            rows={2}
                                                            value={selectedClient?.objective || ''}
                                                            onChange={(e) =>
                                                              patchClient(client.id, {
                                                                objective: e.target.value,
                                                              })
                                                            }
                                                          />
                                                        </label>
                                                        <label className="rec-envio-proposta-field">
                                                          <span className="rec-envio-proposta-label">
                                                            Tempo de Resposta
                                                          </span>
                                                          <input
                                                            className="rec-envio-proposta-input"
                                                            value={selectedClient?.responseTime || ''}
                                                            onChange={(e) =>
                                                              patchClient(client.id, {
                                                                responseTime: e.target.value,
                                                              })
                                                            }
                                                          />
                                                        </label>
                                                      </div>
                                                      <div className="prosp-detail-actions">
                                                        <button
                                                          type="button"
                                                          className="rec-btn rec-btn-primary"
                                                          onClick={() =>
                                                            void saveClient(selectedClient || client)
                                                          }
                                                        >
                                                          Guardar
                                                        </button>
                                                      </div>
                                                    </section>
                                                  </div>
                                                  <aside className="rec-lead-sidebar">
                                                    <div className="rec-lead-workboard">
                                                      <h4 className="rec-lead-section-title">Ações</h4>
                                                      <div className="rec-lead-workboard-row">
                                                        <label className="rec-lead-field">
                                                          <span>Alterar estado</span>
                                                          <select
                                                            className="rec-select"
                                                            value={
                                                              selectedClient?.substage || client.substage
                                                            }
                                                            onChange={(e) =>
                                                              void changeClientSubstage(
                                                                client.id,
                                                                e.target.value
                                                              )
                                                            }
                                                          >
                                                            {RETENCAO_SECTIONS.map((section) => (
                                                              <optgroup
                                                                key={section.key}
                                                                label={section.title}
                                                              >
                                                                {section.subsections.map((subOption) => (
                                                                  <option
                                                                    key={subOption.substage}
                                                                    value={subOption.substage}
                                                                  >
                                                                    {subOption.label}
                                                                  </option>
                                                                ))}
                                                              </optgroup>
                                                            ))}
                                                          </select>
                                                        </label>
                                                        <button
                                                          type="button"
                                                          className="rec-btn rec-btn-solicitar-proposta prosp-lead-action"
                                                          disabled={
                                                            !canRequestOnClient(selectedClient || client) ||
                                                            preparingProposta ||
                                                            preparingContrato
                                                          }
                                                          onClick={() =>
                                                            void handleSolicitarProposta(
                                                              selectedClient || client
                                                            )
                                                          }
                                                        >
                                                          {preparingProposta
                                                            ? 'A preparar…'
                                                            : 'Solicitar Proposta'}
                                                        </button>
                                                        {(selectedClient?.substage || client.substage) ===
                                                          'retencao-fecho-contrato-submetido' && (
                                                          <button
                                                            type="button"
                                                            className="rec-btn rec-btn-submeter-contrato prosp-lead-action"
                                                            disabled={
                                                              !canRequestOnClient(selectedClient || client) ||
                                                              preparingContrato ||
                                                              preparingProposta
                                                            }
                                                            onClick={() =>
                                                              void handleSubmeterContrato(
                                                                selectedClient || client
                                                              )
                                                            }
                                                          >
                                                            {preparingContrato
                                                              ? 'A preparar…'
                                                              : 'Submeter Contrato'}
                                                          </button>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </aside>
                                                </div>
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            {totalRows > CLIENTS_PER_PAGE && (
                              <nav
                                className="prosp-pagination"
                                aria-label={`Paginação — ${sub.label}`}
                              >
                                <button
                                  type="button"
                                  className="rec-btn rec-btn-ghost prosp-pagination-btn"
                                  disabled={currentPage <= 1}
                                  onClick={() => setSubsectionPage(sub.substage, currentPage - 1)}
                                >
                                  Anterior
                                </button>
                                <span className="prosp-pagination-meta">
                                  A mostrar {pageStart}–{pageEnd} de {totalRows} · Página {currentPage}{' '}
                                  de {totalPages}
                                </span>
                                <button
                                  type="button"
                                  className="rec-btn rec-btn-ghost prosp-pagination-btn"
                                  disabled={currentPage >= totalPages}
                                  onClick={() => setSubsectionPage(sub.substage, currentPage + 1)}
                                >
                                  Seguinte
                                </button>
                              </nav>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </div>
        </main>
      </div>

      {showCreate && (
        <div className="rec-modal-backdrop" onClick={() => setShowCreate(false)} role="presentation">
          <div
            className="rec-modal prosp-create-modal retencao-create-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="retencao-create-modal-title"
          >
            <div className="rec-modal-head prosp-create-modal-head retencao-create-modal-head">
              <div className="prosp-create-head-wrap">
                <h2 id="retencao-create-modal-title" className="rec-modal-title">
                  Novo Cliente
                </h2>
                <p className="retencao-create-subtitle">
                  Registe um cliente para a área de Reposição. Campos com * são obrigatórios.
                </p>
              </div>
              <button
                type="button"
                className="prosp-create-close"
                onClick={() => setShowCreate(false)}
                aria-label="Fechar modal"
              >
                ×
              </button>
            </div>
            <div className="rec-modal-body prosp-create-modal-body retencao-create-modal-body">
              <label className="retencao-create-field">
                <span className="prosp-create-label-title">
                  Nome do Cliente <span className="rec-required-asterisk">*</span>
                </span>
                <input
                  className="rec-input"
                  value={draft.company}
                  onChange={(e) => setDraft((prev) => ({ ...prev, company: e.target.value }))}
                />
              </label>
              <label className="retencao-create-field">
                <span className="prosp-create-label-title">
                  NIF <span className="rec-required-asterisk">*</span>
                </span>
                <input
                  className="rec-input"
                  value={draft.nif}
                  onChange={(e) => setDraft((prev) => ({ ...prev, nif: e.target.value }))}
                />
              </label>
              <label className="retencao-create-field">
                <span className="prosp-create-label-title">
                  Data do Cancelamento <span className="rec-required-asterisk">*</span>
                </span>
                <input
                  type="date"
                  className="rec-input"
                  value={draft.cancellationDate}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, cancellationDate: e.target.value }))
                  }
                />
              </label>
              <label className="retencao-create-field">
                <span className="prosp-create-label-title">Motivo de Cancelamento</span>
                <input
                  className="rec-input"
                  value={draft.cancellationReason}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, cancellationReason: e.target.value }))
                  }
                />
              </label>
              <label className="retencao-create-field">
                <span className="prosp-create-label-title">Multa</span>
                <input
                  className="rec-input"
                  value={draft.fine}
                  onChange={(e) => setDraft((prev) => ({ ...prev, fine: e.target.value }))}
                />
              </label>
              <label className="retencao-create-field retencao-create-field--full">
                <span className="prosp-create-label-title">Objectivo</span>
                <textarea
                  className="rec-input retencao-create-textarea"
                  rows={3}
                  value={draft.objective}
                  onChange={(e) => setDraft((prev) => ({ ...prev, objective: e.target.value }))}
                />
              </label>
              <label className="retencao-create-field">
                <span className="prosp-create-label-title">Tempo de Resposta</span>
                <input
                  className="rec-input"
                  value={draft.responseTime}
                  onChange={(e) => setDraft((prev) => ({ ...prev, responseTime: e.target.value }))}
                />
              </label>
              <div className="rec-modal-actions retencao-create-modal-actions">
                <button type="button" className="rec-btn rec-btn-ghost" onClick={() => setShowCreate(false)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="rec-btn rec-btn-primary retencao-create-submit"
                  onClick={() => void createClient()}
                >
                  Criar Cliente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {canManageWorkspaceScope && (
        <Drawer
          title={<Text strong style={{ fontSize: 20 }}>Equipa</Text>}
          placement="left"
          closable
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width={isMobileOrTablet ? '80vw' : 300}
          style={{
            borderTopRightRadius: 18,
            borderBottomRightRadius: 18,
          }}
          headerStyle={{
            borderBottom: '1px solid #f0f0f0',
            background: '#fff',
            borderTopRightRadius: 18,
          }}
          bodyStyle={{
            padding: '16px',
            background: '#fff',
            maxHeight: '100vh',
            overflowY: 'auto',
          }}
        >
          <div className="search-section" style={{ marginBottom: '16px' }}>
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Pesquisar utilizador..."
                value={sidebarSearchTerm}
                onChange={(e) => setSidebarSearchTerm(e.target.value)}
                className="user-search-input"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d9d9d9',
                }}
              />
            </div>
          </div>
          <div className="user-list">
            <div
              className={`user-item ${workspaceUserId === 'all' || workspaceUserId === null ? 'selected' : ''}`}
              onClick={() => {
                setWorkspaceUserId('all');
                setDrawerOpen(false);
              }}
              style={{
                cursor: 'pointer',
                padding: '12px',
                marginBottom: '8px',
                borderRadius: '8px',
                border:
                  workspaceUserId === 'all' || workspaceUserId === null
                    ? '2px solid #1890ff'
                    : '1px solid #f0f0f0',
                backgroundColor:
                  workspaceUserId === 'all' || workspaceUserId === null ? '#e6f7ff' : '#fff',
                transition: 'all 0.2s',
              }}
            >
              <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                  className="user-color"
                  style={{
                    backgroundColor: '#1890ff',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    flexShrink: 0,
                  }}
                />
                <span className="user-name" style={{ flex: 1, fontWeight: 500 }}>
                  Toda a Equipa
                </span>
              </div>
            </div>
            {filteredSidebarUsers.map((option) => (
              <div
                key={option.id}
                className={`user-item ${workspaceUserId === option.id ? 'selected' : ''}`}
                onClick={() => {
                  setWorkspaceUserId(option.id);
                  setDrawerOpen(false);
                }}
                style={{
                  cursor: 'pointer',
                  padding: '12px',
                  marginBottom: '8px',
                  borderRadius: '8px',
                  border: workspaceUserId === option.id ? '2px solid #1890ff' : '1px solid #f0f0f0',
                  backgroundColor: workspaceUserId === option.id ? '#e6f7ff' : '#fff',
                  transition: 'all 0.2s',
                }}
              >
                <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    className="user-color"
                    style={{
                      backgroundColor: option.id % 2 === 0 ? '#ff001e' : '#cccccc',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}
                  />
                  <span className="user-name" style={{ flex: 1, fontWeight: 500 }}>
                    {option.name || option.username || 'Unknown'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Drawer>
      )}

      {gestaoModal && (
        <CccGestaoModalsOverlay
          key={`${gestaoModal}-${gestaoModalNonce}`}
          modal={gestaoModal}
          onClose={() => setGestaoModal(null)}
        />
      )}

      {solicitarPropostaClient && (
        <CccSolicitarPropostaRetencaoClientOverlay
          client={{
            id: solicitarPropostaClient.id,
            company: solicitarPropostaClient.company,
            nif: solicitarPropostaClient.nif,
          }}
          marcacaoId={solicitarPropostaClient.marcacaoId}
          onClose={() => setSolicitarPropostaClient(null)}
          onSuccess={() => void handleProposalSuccess()}
        />
      )}

      {submeterContratoClient && (
        <CccSubmeterContratoRetencaoClientOverlay
          client={{
            id: submeterContratoClient.id,
            company: submeterContratoClient.company,
            nif: submeterContratoClient.nif,
          }}
          marcacaoId={submeterContratoClient.marcacaoId}
          onClose={() => setSubmeterContratoClient(null)}
          onSuccess={() => void handleContractSuccess()}
        />
      )}

      {canSeePool && (
        <>
          <CccAlterarComercialModal
            open={assignModalOpen}
            users={workspaceUsers}
            submitting={bulkSubmitting}
            title="Atribuir a utilizador CCC"
            transferHint="O cliente passará para a área de trabalho de:"
            confirmLabel="Atribuir"
            onClose={() => !bulkSubmitting && setAssignModalOpen(false)}
            onConfirm={handleConfirmAssign}
          />
          <CccAlterarComercialModal
            open={reassignModalOpen}
            users={workspaceUsers}
            submitting={bulkSubmitting}
            title="Reatribuir clientes"
            transferHint="Os clientes passarão para a área de trabalho de:"
            confirmLabel="Reatribuir"
            onClose={() => !bulkSubmitting && setReassignModalOpen(false)}
            onConfirm={handleConfirmReassign}
          />
        </>
      )}
    </div>
  );
};

export default RetencaoBoard;
